import React, { useEffect, useState } from 'react';

import { Button, DatePicker, Tag, Table, message } from 'antd';
import moment, { Moment } from 'moment';
import styled from 'styled-components';

import {
  getReconciliationHistory,
  ReconciliationHistoryEntry,
} from '../../../hooks/reconciliation';
import { hasReconciliationRole } from '../../../utils/hasReconciliationRole/hasReconciliationRole';
import ReconciliationUploadModal from '../../organisms/ReconciliationUploadModal';

const Div = styled.div`
  padding: 16px 0;
`;

const Subtitle = styled.div`
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
  margin-bottom: 16px;
`;

interface ReconciliationHistoryPageProps {
  /**
   * Quando esta prop vira {@code true}, a página re-executa o fetch da lista.
   * Permite ao parent (ex: Tabs do antd) sinalizar que a aba voltou a ficar
   * visível e os dados devem ser atualizados.
   */
  isActive?: boolean;
}

/**
 * MV2-1909: tela de Histórico de Conciliações.
 * Lista competências processadas por este tenant (uma por mês). "Ver detalhes"
 * abre o modal de conciliação em modo view (sem upload).
 */
const ReconciliationHistoryPage: React.FC<ReconciliationHistoryPageProps> = ({ isActive = true }) => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<ReconciliationHistoryEntry[]>([]);
  const [page, setPage] = useState(1);          // antd Table usa 1-indexed
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [fromMonth, setFromMonth] = useState<Moment | null>(null);
  const [toMonth, setToMonth] = useState<Moment | null>(null);
  const [viewPeriod, setViewPeriod] = useState<string | null>(null);

  const hasRole = hasReconciliationRole();

  const load = async (
    p = page,
    sz = pageSize,
    from = fromMonth,
    to = toMonth,
  ) => {
    setLoading(true);
    try {
      // Backend é 0-indexed; UI é 1-indexed. Datas em formato YYYY-MM.
      const data = await getReconciliationHistory(
        p - 1,
        sz,
        from ? from.format('YYYY-MM') : null,
        to ? to.format('YYYY-MM') : null,
      );
      setEntries(data.entries);
      setTotal(data.total);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Falha ao carregar histórico.');
    } finally {
      setLoading(false);
    }
  };

  // Re-executa fetch toda vez que a aba volta a ficar ativa (incluindo o mount).
  useEffect(() => {
    if (!isActive) return;
    load(page, pageSize, fromMonth, toMonth);
    /* eslint-disable-next-line */
  }, [isActive]);

  if (!hasRole) {
    return (
      <Div>
        <Subtitle>Você não tem permissão para visualizar esta tela.</Subtitle>
      </Div>
    );
  }

  const formatPeriod = (p: string): string => {
    // "2026-08" → "Agosto/26"
    const [y, m] = p.split('-');
    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const monthIdx = parseInt(m, 10) - 1;
    return `${monthNames[monthIdx] || m}/${y.slice(2)}`;
  };

  const columns = [
    {
      title: 'Competência',
      dataIndex: 'referencePeriod',
      key: 'referencePeriod',
      render: (p: string) => <strong>{formatPeriod(p)}</strong>,
    },
    {
      title: 'Processado em',
      dataIndex: 'lastProcessedAt',
      key: 'lastProcessedAt',
      render: (ts: string | null) => ts
        ? <span style={{ color: 'rgba(0,0,0,.65)' }}>{moment(ts).format('DD/MM/YYYY HH:mm')}</span>
        : <span style={{ color: 'rgba(0,0,0,.25)' }}>—</span>,
    },
    {
      title: 'Responsável',
      dataIndex: 'lastActorName',
      key: 'lastActorName',
      render: (name: string | null, row: ReconciliationHistoryEntry) =>
        name || row.lastActorEmail || <span style={{ color: 'rgba(0,0,0,.25)' }}>—</span>,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right' as const,
    },
    {
      title: 'Conciliadas',
      dataIndex: 'reconciled',
      key: 'reconciled',
      align: 'right' as const,
    },
    {
      title: 'Divergências',
      dataIndex: 'divergence',
      key: 'divergence',
      align: 'right' as const,
    },
    {
      title: 'Não conciliadas',
      dataIndex: 'unreconciled',
      key: 'unreconciled',
      align: 'right' as const,
    },
    {
      title: 'Status',
      dataIndex: 'fullyReconciled',
      key: 'fullyReconciled',
      render: (full: boolean) => full
        ? <Tag color='green'>Conciliado</Tag>
        : <Tag color='gold'>Pendente</Tag>,
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_: unknown, row: ReconciliationHistoryEntry) => (
        <Button type='link' onClick={() => setViewPeriod(row.referencePeriod)}>
          Ver detalhes
        </Button>
      ),
    },
  ];

  return (
    <Div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
        <Subtitle style={{ marginBottom: 0 }}>
          Um registro por competência processada, com quem rodou e o resultado.
        </Subtitle>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ color: 'rgba(0,0,0,.45)', fontSize: 12 }}>Competência:</span>
          <DatePicker
            picker='month'
            placeholder='De'
            value={fromMonth}
            format='MM/YYYY'
            disabledDate={(d) => !!toMonth && !!d && d.isAfter(toMonth, 'month')}
            onChange={(d) => setFromMonth(d)}
          />
          <DatePicker
            picker='month'
            placeholder='Até'
            value={toMonth}
            format='MM/YYYY'
            disabledDate={(d) => !!fromMonth && !!d && d.isBefore(fromMonth, 'month')}
            onChange={(d) => setToMonth(d)}
          />
          <Button
            type='primary'
            onClick={() => {
              setPage(1);
              load(1, pageSize, fromMonth, toMonth);
            }}
          >
            Filtrar
          </Button>
          {(fromMonth || toMonth) && (
            <Button
              onClick={() => {
                setFromMonth(null);
                setToMonth(null);
                setPage(1);
                load(1, pageSize, null, null);
              }}
            >
              Limpar
            </Button>
          )}
        </div>
      </div>

      <Table
        rowKey='referencePeriod'
        loading={loading}
        dataSource={entries}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (t) => `${t} competência(s)`,
        }}
        onChange={(p) => {
          const newPage = p.current || 1;
          const newSize = p.pageSize || pageSize;
          setPage(newPage);
          setPageSize(newSize);
          load(newPage, newSize, fromMonth, toMonth);
        }}
        locale={{ emptyText: 'Nenhuma conciliação processada ainda.' }}
      />

      <ReconciliationUploadModal
        visible={viewPeriod !== null}
        viewPeriod={viewPeriod}
        onClose={() => {
          setViewPeriod(null);
          load(page, pageSize, fromMonth, toMonth);
        }}
      />
    </Div>
  );
};

export default ReconciliationHistoryPage;
